import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';

import { api } from '../api';
import { invalidateEventQueries, optimisticAddEvent } from '../event-cache';
import { isoForEvent } from '../event-day';
import type { Calendar, CountdownMode, Preferences } from '../types';
import CenteredOverlay from './CenteredOverlay';

// The parser runs on the backend (rialto/quickadd.py), shared with the
// Android app. Token offsets index the text as JavaScript does.

type TokenKind =
  | 'date'
  | 'time'
  | 'duration'
  | 'allDay'
  | 'countdown'
  | 'calendar'
  | 'quote'
  | 'location'
  | 'recurrence';

interface Token {
  kind: TokenKind;
  start: number;
  end: number;
  text: string;
  calendarId: string | null;
}

interface ParseResponse {
  text: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  tokens: Token[];
  calendarId: string | null;
  hasQuotedTitle: boolean;
  location: string | null;
  recurrence: { rrule: string; description: string } | null;
  countdown: boolean;
}

type Parsed = Omit<ParseResponse, 'start' | 'end'> & { start: Date; end: Date };

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

async function parseText(text: string): Promise<Parsed> {
  const r = await api.post<ParseResponse>('/api/quick-add/parse', { text, tz: TIME_ZONE });
  return { ...r, start: new Date(r.start), end: new Date(r.end) };
}

// What the panel shows before the first answer arrives: today, all day.
function emptyParse(text: string): Parsed {
  const today = startOfDay(new Date());
  return {
    text,
    title: '',
    start: today,
    end: addDays(today, 1),
    allDay: true,
    tokens: [],
    calendarId: null,
    hasQuotedTitle: false,
    location: null,
    recurrence: null,
    countdown: false
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  calendars: Calendar[];
}

// Pill colors. `bg` paints the inline highlight inside the input overlay; `dot`
// paints the matching dot next to each detail row below.
const TOKEN_STYLE: Record<TokenKind, { bg: string; dot: string; label: string }> = {
  date:     { bg: 'hsl(210 90% 65% / 0.32)', dot: 'hsl(210 90% 60%)',  label: 'Date' },
  time:     { bg: 'hsl(30 95% 60% / 0.38)',  dot: 'hsl(30 95% 55%)',   label: 'Time' },
  duration: { bg: 'hsl(150 70% 55% / 0.36)', dot: 'hsl(150 60% 45%)',  label: 'Duration' },
  allDay:   { bg: 'hsl(280 70% 70% / 0.36)', dot: 'hsl(280 70% 60%)',  label: 'All-day' },
  countdown:  { bg: 'hsl(0 85% 62% / 0.34)',   dot: 'hsl(0 80% 55%)',    label: 'Countdown' },
  calendar:   { bg: 'hsl(340 80% 65% / 0.32)', dot: 'hsl(340 80% 55%)',  label: 'Calendar' },
  quote:      { bg: 'hsl(0 0% 60% / 0.22)',    dot: 'hsl(0 0% 50%)',     label: 'Quoted title' },
  location:   { bg: 'hsl(180 70% 55% / 0.32)', dot: 'hsl(180 65% 45%)',  label: 'Location' },
  recurrence: { bg: 'hsl(50 90% 60% / 0.36)',  dot: 'hsl(45 90% 50%)',   label: 'Repeat' }
};

function renderTokens(text: string, tokens: Token[]): React.ReactNode {
  if (text.length === 0) return null;
  if (tokens.length === 0) return text;
  const sorted = [...tokens].sort((a, b) => a.start - b.start);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < sorted.length; i++) {
    const t = sorted[i];
    if (t.start > cursor) out.push(<span key={`p${i}`}>{text.slice(cursor, t.start)}</span>);
    const style = TOKEN_STYLE[t.kind];
    out.push(
      <span
        key={`t${i}`}
        className="rounded-[6px]"
        style={{ background: style.bg, boxShadow: `0 0 0 1px ${style.bg}` }}
      >
        {text.slice(t.start, t.end)}
      </span>
    );
    cursor = t.end;
  }
  if (cursor < text.length) out.push(<span key="tail">{text.slice(cursor)}</span>);
  return out;
}

export default function QuickAdd({ open, onClose, calendars }: Props) {
  const qc = useQueryClient();
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences')
  });
  const writable = useMemo(
    () => calendars.filter((c) => c.access_role === 'owner' || c.access_role === 'writer'),
    [calendars]
  );
  const defaultPrefId = prefsQ.data?.default_calendar_id ?? null;
  const defaultCal =
    (defaultPrefId && writable.find((c) => c.id === defaultPrefId)) ||
    writable.find((c) => c.is_primary) ||
    writable[0];

  const [text, setText] = useState('');
  const [calendarId, setCalendarId] = useState(defaultCal?.id ?? '');
  // Tracks whether the user has manually picked a calendar in this session —
  // once they have, we stop overriding it from parser matches.
  const [calendarManual, setCalendarManual] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setText('');
    setCalendarManual(false);
    if (defaultCal) setCalendarId(defaultCal.id);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const input = inputRef.current;
    const overlay = overlayRef.current;
    if (!input || !overlay) return;
    function onScroll() {
      if (overlay && input) overlay.scrollLeft = input.scrollLeft;
    }
    input.addEventListener('scroll', onScroll);
    return () => input.removeEventListener('scroll', onScroll);
  }, [open]);

  // Parse once typing pauses; the last answer stays up meanwhile.
  const [debounced, setDebounced] = useState(text);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text), 150);
    return () => window.clearTimeout(t);
  }, [text]);
  const parseQ = useQuery({
    queryKey: ['quick-add', debounced],
    queryFn: () => parseText(debounced),
    enabled: open,
    placeholderData: keepPreviousData,
    staleTime: 60_000
  });
  const parsed = parseQ.data ?? emptyParse(text);

  useEffect(() => {
    if (!open || calendarManual) return;
    if (parsed.calendarId && parsed.calendarId !== calendarId) {
      setCalendarId(parsed.calendarId);
    }
  }, [parsed.calendarId, calendarManual, calendarId, open]);

  // A repeating event counts down to whichever occurrence is next; a one-off
  // counts down to its own day.
  const modeFor = (p: Parsed): CountdownMode | null =>
    p.countdown ? (p.recurrence ? 'series' : 'once') : null;
  const countdownMode = modeFor(parsed);

  const hasDate = parsed.tokens.some((t) => t.kind === 'date');
  const hasTime = parsed.tokens.some((t) => t.kind === 'time');
  const hasDuration = parsed.tokens.some((t) => t.kind === 'duration');
  const hasQuote = parsed.hasQuotedTitle;

  const create = useMutation({
    mutationFn: ({ p, calId }: { p: Parsed; calId: string }) =>
      api.post<{ id: string }>('/api/events', {
        calendar_id: calId,
        summary: p.title,
        location: p.location,
        description: null,
        all_day: p.allDay,
        start_time: isoForEvent(p.start, p.allDay),
        end_time: isoForEvent(p.end, p.allDay),
        recurrence: p.recurrence ? [`RRULE:${p.recurrence.rrule}`] : null,
        countdown: modeFor(p)
      }),
    onSettled: () => {
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['countdowns'] });
    }
  });

  async function submit() {
    // The panel may still show the answer for slightly older text.
    let p: Parsed;
    try {
      p =
        parseQ.data && parseQ.data.text === text
          ? parseQ.data
          : await qc.fetchQuery({ queryKey: ['quick-add', text], queryFn: () => parseText(text) });
    } catch {
      return;
    }
    const calId = calendarManual ? calendarId : p.calendarId ?? calendarId;
    if (!calId || !p.title) return;
    const cal = calendars.find((c) => c.id === calId);
    optimisticAddEvent(qc, {
      id: crypto.randomUUID(),
      calendar_id: calId,
      summary: p.title,
      location: p.location,
      start_time: isoForEvent(p.start, p.allDay),
      end_time: isoForEvent(p.end, p.allDay),
      all_day: p.allDay,
      status: 'confirmed',
      html_link: null,
      is_recurring_instance: false,
      countdown: modeFor(p),
      background_color: cal?.background_color ?? null,
      foreground_color: cal?.foreground_color ?? null
    });
    create.mutate({ p, calId });
    onClose();
  }

  if (!open) return null;

  // All-day end is exclusive, so the displayed last day is one earlier.
  const lastDay = parsed.allDay ? addDays(parsed.end, -1) : parsed.end;
  // The year is always shown — the parser can land a date years out, and the
  // row is where you check that it read you right. A span carries it once at
  // the end when both days share a year.
  const sameYear = parsed.start.getFullYear() === lastDay.getFullYear();
  const dateLabel = isSameDay(parsed.start, lastDay)
    ? format(parsed.start, 'EEE, MMM d, yyyy')
    : `${format(parsed.start, sameYear ? 'EEE, MMM d' : 'EEE, MMM d, yyyy')} – ${format(lastDay, 'EEE, MMM d, yyyy')}`;
  const timeLabel = parsed.allDay
    ? 'all-day'
    : `${format(parsed.start, 'h:mm a')} – ${format(parsed.end, 'h:mm a')}`;
  const durationMinutes = Math.round((parsed.end.getTime() - parsed.start.getTime()) / 60_000);
  const matchedCalendar = parsed.calendarId
    ? writable.find((c) => c.id === parsed.calendarId)
    : null;

  return (
    <CenteredOverlay ariaLabel="Quick add event" onClose={onClose} maxWidth={560}>
      <div className="border-b border-[var(--border)] px-4 py-3">
          <div className="relative" style={{ height: '36px' }}>
            <div
              ref={overlayRef}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre text-[1.05rem]"
              style={{ lineHeight: '36px', letterSpacing: '0' }}
            >
              {renderTokens(text, text.startsWith(parsed.text) ? parsed.tokens : [])}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder='e.g. tomorrow 3-5pm meetings "Q3 planning"'
              className="absolute inset-0 w-full bg-transparent text-[1.05rem] text-transparent caret-[var(--text)] outline-none placeholder:text-[var(--text-faint)]"
              style={{ lineHeight: '36px', letterSpacing: '0' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-sm">
          <DetailLabel kind="calendar" active={matchedCalendar !== null} />
          <select
            value={calendarId}
            onChange={(e) => {
              setCalendarId(e.target.value);
              setCalendarManual(true);
            }}
            className="w-full min-w-0 rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[4px] text-sm"
          >
            {writable.length === 0 && <option value="">No writable calendars</option>}
            {writable.map((c) => (
              <option key={c.id} value={c.id}>
                {c.summary} — {c.account_email}
              </option>
            ))}
          </select>

          <span className="self-center text-[var(--text-muted)]">Title</span>
          <span className="self-center text-[var(--text)]">
            {parsed.title || <span className="text-[var(--text-faint)]">(start typing…)</span>}
            {hasQuote && (
              <span className="ml-2 text-[0.7rem] text-[var(--text-faint)]">(quoted)</span>
            )}
          </span>

          <DetailLabel kind="location" active={parsed.location !== null} />
          <span className="self-center text-[var(--text)]">
            {parsed.location ?? <span className="text-[var(--text-faint)]">—</span>}
          </span>

          <DetailLabel kind="date" active={hasDate} />
          <span className="self-center text-[var(--text)]">{dateLabel}</span>

          <DetailLabel kind={parsed.allDay ? 'allDay' : 'time'} active={hasTime || parsed.allDay} />
          <span className="self-center text-[var(--text)]">
            {timeLabel}
            {hasDuration && !parsed.allDay && (
              <span className="ml-2 text-[var(--text-faint)]">({durationMinutes} min)</span>
            )}
            {!hasDuration && hasTime && !parsed.allDay && durationMinutes !== 60 && (
              <span className="ml-2 text-[var(--text-faint)]">({durationMinutes} min)</span>
            )}
          </span>

          <DetailLabel kind="recurrence" active={parsed.recurrence !== null} />
          <span className="self-center text-[var(--text)]">
            {parsed.recurrence?.description ?? <span className="text-[var(--text-faint)]">—</span>}
          </span>

          <DetailLabel kind="countdown" active={parsed.countdown} />
          <span className="self-center text-[var(--text)]">
            {countdownMode ? (
              countdownMode === 'series' ? (
                'Sidebar badge, next occurrence'
              ) : (
                'Sidebar badge'
              )
            ) : (
              <span className="text-[var(--text-faint)]">—</span>
            )}
          </span>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!parsed.title || !calendarId}
            className="rounded-md bg-[var(--accent)] px-3 py-[6px] text-[0.8rem] text-[var(--accent-fg)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create
          </button>
        </footer>
    </CenteredOverlay>
  );
}

function DetailLabel({ kind, active }: { kind: TokenKind; active: boolean }) {
  const s = TOKEN_STYLE[kind];
  return (
    <span className="flex items-center gap-[6px] self-center text-[var(--text-muted)]">
      <span
        aria-hidden
        className="h-[8px] w-[8px] rounded-full"
        style={{ background: active ? s.dot : 'var(--border-strong)' }}
      />
      {s.label}
    </span>
  );
}
